/**
 * Base path / base URL — single source of truth derived from the current pathname.
 * No Next.js or React; used by proxy (rewrite), BasePathProvider, api-client (401 redirect),
 * and server code when the full request URL is available.
 * Rule: pathname not under /c/{companyId} → base "/"; pathname under /c/{companyId} → base "/c/{companyId}".
 */

/** Path prefix for company-scoped routes: /c/{companyId} */
export const COMPANY_PATH_PREFIX = '/c/';

/**
 * Extracts the company segment from a pathname matching /c/{segment}. Used internally by
 * getBasePath/getBaseUrl — routing only, and deliberately unexported: the segment is not a
 * company identifier for the API, matching the companyId for some tenants (/c/demo → 'demo')
 * but not others (PB-5008). The route's company comes from the resolved theme.
 */
function getCompanyIdFromPathname(pathname: string): string | null {
  if (!pathname.startsWith(COMPANY_PATH_PREFIX)) return null;
  const after = pathname.slice(COMPANY_PATH_PREFIX.length);
  const segment = after.split('/')[0];
  return segment && segment.length > 0 ? segment : null;
}

/**
 * Returns the base path (pathname only) for the app. Use for Link href or router.push.
 * - If pathname starts with /c/{companyId}, returns "/c/{companyId}" (no trailing slash).
 * - Otherwise returns "/".
 */
export function getBasePath(pathname: string): string {
  const companyId = getCompanyIdFromPathname(pathname);
  if (companyId) {
    return `${COMPANY_PATH_PREFIX}${companyId}`;
  }
  return '/';
}

/**
 * Returns the app base URL for the given origin and pathname.
 * - If pathname starts with /c/{companyId}, returns {origin}/c/{companyId} (no trailing slash).
 * - Otherwise returns {origin}/.
 *
 * @param origin - e.g. "http://localhost:3000" or "https://example.com"
 * @param pathname - e.g. "/", "/login", "/c/rutgers", "/c/rutgers/settings"
 */
export function getBaseUrl(origin: string, pathname: string): string {
  const companyId = getCompanyIdFromPathname(pathname);
  if (companyId) {
    return `${origin}${COMPANY_PATH_PREFIX}${companyId}`;
  }
  return `${origin}/`;
}

/**
 * Strips the /c/{companyId} prefix from a pathname if present.
 * E.g. "/c/demo/myaccount/profile" → "/myaccount/profile", "/" stays "/".
 */
export function stripCompanyPrefix(pathname: string): string {
  const companyId = getCompanyIdFromPathname(pathname);
  if (!companyId) return pathname;
  const prefixLength = COMPANY_PATH_PREFIX.length + companyId.length;
  const rest = pathname.slice(prefixLength);
  return rest === '' ? '/' : rest;
}

/**
 * Returns the app base URL derived from an absolute URL.
 * Use on the server when you have the full request URL (e.g. from getCurrentRequestUrl()).
 *
 * @param url - Full URL string (e.g. "http://localhost:3000/c/rutgers/settings")
 */
export function getBaseUrlFromAbsoluteUrl(url: string): string {
  const parsed = new URL(url);
  return getBaseUrl(parsed.origin, parsed.pathname);
}
